// Golf Match Play Tracker 2026 -- Points & standings
//
// =====================================================================
// POINTS
//   Win     5
//   Tie     3
//   Loss    1
//   Forfeit 0 to the player who forfeits; their opponent receives 3.
//
// TIEBREAKERS (applied in order, only between players tied on points)
//   1. Head-to-head result. If their match was a tie, move on.
//   2. Most wins.
//   3. Most combined points from WINS and TIES only  (wins*5 + ties*3).
//   4. 40yd dash down #1 / chip-off on the practice green. This happens
//      on grass, so the admin records the outcome in the Standings tab
//      and it is honoured here.
//
// NOTE ON FORFEITS AND TIEBREAKERS 2 & 3
//   A forfeit win is worth 3 points (the same as a tie), not 5, so it is
//   NOT counted as a "win" for tiebreaker 2, and the 3 points are not
//   counted in tiebreaker 3 ("wins & ties only"). Both figures are shown
//   separately in the standings table so nothing is hidden. If you want
//   forfeit wins to count as wins, flip COUNT_FORFEIT_WIN_AS_WIN below.
// =====================================================================

import {
  POINTS,
  POINTS_OPPONENT_OF_FORFEIT,
  DIVISIONS,
  PLAYOFF_QUALIFIERS_PER_DIVISION,
} from "./constants";
import { pairsFromPairings } from "./helpers";

export const COUNT_FORFEIT_WIN_AS_WIN = false;

/**
 * Build the full stat line for every golfer.
 * Only REGULAR SEASON matches count toward points and standings.
 */
export function computeStats(golfers, matches) {
  const stats = new Map();
  golfers.forEach(g => stats.set(g.id, {
    golfer: g,
    points: g.points_adjust || 0,
    wins: 0, ties: 0, losses: 0,
    forfeitsGiven: 0, forfeitsReceived: 0,
    matchesPlayed: 0,
    // head-to-head ledger: opponentId -> 'win' | 'loss' | 'tie' | 'forfeit_win' | 'forfeit_loss'
    h2h: new Map(),
  }));

  const regular = (matches || []).filter(m => (m.phase || "regular") === "regular");

  for (const m of regular) {
    for (const [a, b] of pairsFromPairings(m.pairings)) {
      const sa = stats.get(a);
      const sb = stats.get(b);
      if (!sa || !sb) continue;

      const ra = m.results?.[a] || "pending";
      const rb = m.results?.[b] || "pending";
      if (ra === "pending" && rb === "pending") continue;

      const aForfeit = ra === "forfeit";
      const bForfeit = rb === "forfeit";

      // ---- Forfeits ----
      if (aForfeit || bForfeit) {
        if (aForfeit) {
          sa.points += POINTS.forfeit;      // 0
          sa.forfeitsGiven += 1;
          sa.h2h.set(b, "forfeit_loss");
        }
        if (bForfeit) {
          sb.points += POINTS.forfeit;      // 0
          sb.forfeitsGiven += 1;
          sb.h2h.set(a, "forfeit_loss");
        }
        // Opponent of a forfeit receives 3 (unless they also forfeited)
        if (aForfeit && !bForfeit) {
          sb.points += POINTS_OPPONENT_OF_FORFEIT;
          sb.forfeitsReceived += 1;
          sb.h2h.set(a, "forfeit_win");
          if (COUNT_FORFEIT_WIN_AS_WIN) sb.wins += 1;
        }
        if (bForfeit && !aForfeit) {
          sa.points += POINTS_OPPONENT_OF_FORFEIT;
          sa.forfeitsReceived += 1;
          sa.h2h.set(b, "forfeit_win");
          if (COUNT_FORFEIT_WIN_AS_WIN) sa.wins += 1;
        }
        sa.matchesPlayed += 1;
        sb.matchesPlayed += 1;
        continue;
      }

      // ---- Normal result ----
      if (ra === "pending" || rb === "pending") continue;

      sa.points += POINTS[ra] ?? 0;
      sb.points += POINTS[rb] ?? 0;
      sa.matchesPlayed += 1;
      sb.matchesPlayed += 1;

      if (ra === "win") {
        sa.wins += 1; sb.losses += 1;
        sa.h2h.set(b, "win"); sb.h2h.set(a, "loss");
      } else if (ra === "loss") {
        sa.losses += 1; sb.wins += 1;
        sa.h2h.set(b, "loss"); sb.h2h.set(a, "win");
      } else if (ra === "tie") {
        sa.ties += 1; sb.ties += 1;
        sa.h2h.set(b, "tie"); sb.h2h.set(a, "tie");
      }
    }
  }

  // Derived: tiebreaker #3 value
  stats.forEach(s => {
    s.winTiePoints = s.wins * POINTS.win + s.ties * POINTS.tie;
  });

  return stats;
}

// -------------------------------------------------------------------
// Head-to-head between two players.
// Returns 'a' | 'b' | null (null = tied, forfeit-only, or never played)
// -------------------------------------------------------------------
function headToHead(sa, sb) {
  const r = sa.h2h.get(sb.golfer.id);
  if (!r) return null;
  if (r === "win" || r === "forfeit_win") return "a";
  if (r === "loss" || r === "forfeit_loss") return "b";
  return null; // tie
}

// -------------------------------------------------------------------
// Manual tiebreak (chip-off / 40yd dash) lookup
// -------------------------------------------------------------------
function manualTiebreak(resolutions, aId, bId) {
  for (const r of resolutions || []) {
    if (r.winner_id === aId && r.loser_id === bId) return "a";
    if (r.winner_id === bId && r.loser_id === aId) return "b";
  }
  return null;
}

/**
 * Rank one division, applying the full tiebreaker ladder.
 *
 * Each returned row carries `tiebreakTrail` -- the criteria that decided
 * its position relative to the player above it -- so the standings table
 * can show WHY someone is ahead.
 */
export function rankDivision(divisionGolfers, stats, resolutions = []) {
  const rows = divisionGolfers
    .map(g => stats.get(g.id))
    .filter(Boolean);

  const compare = (sa, sb) => {
    // 0. Points
    if (sb.points !== sa.points) return { diff: sb.points - sa.points, by: "points" };

    // 1. Head-to-head
    const h2h = headToHead(sa, sb);
    if (h2h === "a") return { diff: -1, by: "h2h" };
    if (h2h === "b") return { diff: 1, by: "h2h" };

    // 2. Most wins
    if (sb.wins !== sa.wins) return { diff: sb.wins - sa.wins, by: "wins" };

    // 3. Most combined points from wins & ties only
    if (sb.winTiePoints !== sa.winTiePoints) {
      return { diff: sb.winTiePoints - sa.winTiePoints, by: "winTiePoints" };
    }

    // 4. Chip-off / 40yd dash, recorded by the admin
    const manual = manualTiebreak(resolutions, sa.golfer.id, sb.golfer.id);
    if (manual === "a") return { diff: -1, by: "manual" };
    if (manual === "b") return { diff: 1, by: "manual" };

    return { diff: 0, by: "unresolved" };
  };

  const sorted = rows.slice().sort((sa, sb) => {
    const r = compare(sa, sb);
    if (r.diff !== 0) return r.diff;
    // Stable, deterministic display order when genuinely unresolved.
    const aHI = Number(sa.golfer.handicap_index ?? 99);
    const bHI = Number(sb.golfer.handicap_index ?? 99);
    if (aHI !== bHI) return aHI - bHI;
    return sa.golfer.name.localeCompare(sb.golfer.name);
  });

  const cut = PLAYOFF_QUALIFIERS_PER_DIVISION;

  return sorted.map((s, i) => {
    const prev = i > 0 ? sorted[i - 1] : null;
    const vsPrev = prev ? compare(prev, s) : null;

    // Does this player need a physical tiebreaker to settle their spot?
    const unresolvedAbove = vsPrev && vsPrev.by === "unresolved";
    const next = sorted[i + 1];
    const vsNext = next ? compare(s, next) : null;
    const unresolvedBelow = vsNext && vsNext.by === "unresolved";

    const rank = i + 1;
    // Flag loudly when the unresolved tie straddles the playoff cut line.
    const straddlesCut =
      (unresolvedBelow && rank === cut) || (unresolvedAbove && rank === cut + 1);

    return {
      ...s,
      rank,
      qualifies: rank <= cut,
      decidedBy: vsPrev ? vsPrev.by : null,
      needsTiebreak: !!(unresolvedAbove || unresolvedBelow),
      tiebreakBlocksPlayoff: !!straddlesCut,
      tiedWith: [
        ...(unresolvedAbove ? [prev.golfer.id] : []),
        ...(unresolvedBelow ? [next.golfer.id] : []),
      ],
    };
  });
}

/**
 * Full standings, division by division.
 * @returns { byDivision: { Woods: [rows], ... }, overall: [rows], stats }
 */
export function computeStandings(golfers, matches, resolutions = []) {
  const stats = computeStats(golfers, matches);
  const active = (golfers || []).filter(g => g.active !== false);

  const byDivision = {};
  DIVISIONS.forEach(d => {
    byDivision[d] = rankDivision(active.filter(g => g.division === d), stats, resolutions);
  });

  const overall = Object.values(byDivision)
    .flat()
    .slice()
    .sort((a, b) => b.points - a.points || b.wins - a.wins || a.golfer.name.localeCompare(b.golfer.name))
    .map((r, i) => ({ ...r, overallRank: i + 1 }));

  return { byDivision, overall, stats };
}

/** Top N from each division, in seeded order. */
export function playoffQualifiers(standings) {
  const out = {};
  DIVISIONS.forEach(d => {
    out[d] = (standings.byDivision[d] || []).slice(0, PLAYOFF_QUALIFIERS_PER_DIVISION);
  });
  return out;
}

/** Any division still needing a chip-off before the bracket can be set? */
export function unresolvedTiebreaks(standings) {
  const problems = [];
  DIVISIONS.forEach(d => {
    (standings.byDivision[d] || []).forEach(r => {
      if (r.tiebreakBlocksPlayoff) {
        problems.push({ division: d, golfer: r.golfer, rank: r.rank, tiedWith: r.tiedWith });
      }
    });
  });
  return problems;
}

/**
 * Apply a result to one player and mirror it onto their opponent, so it is
 * impossible to record two winners (or two losers) in the same match.
 *
 *   win     -> opponent loss
 *   loss    -> opponent win
 *   tie     -> opponent tie
 *   pending -> opponent cleared back to pending
 *   forfeit -> opponent UNCHANGED. Their 3 points come from the scoring
 *              engine, not from a result value, so a forfeit is visibly
 *              different from a win.
 */
export function mirrorResult(results, golferId, opponentId, value) {
  const next = { ...(results || {}) };
  next[golferId] = value;
  if (!opponentId) return next;

  if (value === 'win')  next[opponentId] = 'loss';
  else if (value === 'loss') next[opponentId] = 'win';
  else if (value === 'tie')  next[opponentId] = 'tie';
  else if (value === 'pending') {
    const opp = next[opponentId];
    if (opp === 'win' || opp === 'loss' || opp === 'tie') next[opponentId] = 'pending';
  }
  return next;
}

/**
 * Swap one golfer for another inside a match, but only if that player's
 * own match has not been decided yet. Returns null when nothing changes.
 */
export function substituteInMatch(match, outgoingId, incomingId) {
  if (!(match.group_players || []).includes(outgoingId)) return null;
  if ((match.results?.[outgoingId] || 'pending') !== 'pending') return null;

  const swap = (arr) => (arr || []).map(x => (x === outgoingId ? incomingId : x));

  const results = { ...(match.results || {}) };
  delete results[outgoingId];
  results[incomingId] = 'pending';

  const selections = { ...(match.golfer_selections || {}) };
  if (selections[outgoingId]) {
    selections[incomingId] = selections[outgoingId];
    delete selections[outgoingId];
  }

  return {
    ...match,
    group_players: swap(match.group_players),
    pairings: swap(match.pairings),
    results,
    golfer_selections: selections,
  };
}
